/**
 * Documents auth context behavior for ERP Accelerator.
 */

"use client";

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';

interface User {
  id: string;
  username: string;
  role_uuid: string;
  role_name: string;
  org_uuid: string;
  is_admin: boolean;
  is_manager: boolean;
  can_approve: boolean;
  fitgap_enabled: boolean;
  first_name: string;
  last_name: string;
  profile_photo_url?: string;
}

interface Organization {
  id: string;
  name: string;
  code?: string;
  description?: string;
  active?: boolean;
}

interface AuthContextType {
  user: User | null;
  organization: Organization | null;
  profilePhotoUrl: string | null;
  isAuthenticated: boolean;
  isInitialized: boolean;
  isAdmin: boolean;
  isManager: boolean;
  canApprove: boolean;
  fitgapEnabled: boolean;
  login: (token: string, user: User, organization: Organization) => void;
  logout: () => void;
  setCurrentOrganization: (org: Organization) => void;
  fetchProfilePhotoUrl: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Handles auth provider behavior.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  // State variables must be declared first
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const router = useRouter();

  // Functions must come after state variables
  const fetchProfilePhotoUrl = async () => {
    if (!isAuthenticated || !user) {
      console.log('fetchProfilePhotoUrl: Not authenticated or no user, skipping');
      return;
    }

    try {
      console.log('fetchProfilePhotoUrl: Fetching presigned URL for user:', user.username);
      const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8082/api';
      const response = await fetch(`${apiUrl}/user/profile-photo/presigned`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
        },
      });

      console.log('fetchProfilePhotoUrl: Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('fetchProfilePhotoUrl: Response data:', data);

        if (data.success && data.presigned_url) {
          console.log('fetchProfilePhotoUrl: Setting profile photo URL:', data.presigned_url);
          setProfilePhotoUrl(data.presigned_url);
        } else {
          console.log('fetchProfilePhotoUrl: No profile photo found or success=false');
          setProfilePhotoUrl(null);
        }
      } else {
        const errorText = await response.text();
        console.error('fetchProfilePhotoUrl: HTTP error:', response.status, errorText);
        setProfilePhotoUrl(null);
      }
    } catch (error) {
      console.error('fetchProfilePhotoUrl: Network error:', error);
      setProfilePhotoUrl(null);
    }
  };

  const login = (token: string, user: User, organization: Organization) => {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('token', token);
      sessionStorage.setItem('user', JSON.stringify(user));
      sessionStorage.setItem('organization', JSON.stringify(organization));
    }
    setUser(user);
    setOrganization(organization);
    setIsAuthenticated(true);
    // Fetch profile photo URL after login
    fetchProfilePhotoUrl();
    router.replace('/workflow/projects');
  };
  
  const setCurrentOrganization = (org: Organization) => {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('organization', JSON.stringify(org));
    }
    setOrganization(org);
  };

  const logout = () => {
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('organization');
    }
    setUser(null);
    setOrganization(null);
    setIsAuthenticated(false);
    setProfilePhotoUrl(null);
    router.replace('/login');
  };

  // useEffect must come after state variables are declared
  useEffect(() => {
    if (isAuthenticated && user) {
      fetchProfilePhotoUrl();
    } else {
      setProfilePhotoUrl(null);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    // Check for existing auth on mount
    const checkAuth = async () => {
      if (typeof window !== 'undefined') {
        const storedUser = sessionStorage.getItem('user');
        const storedOrg = sessionStorage.getItem('organization');
        
        if (storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);
            setUser(parsedUser);
            setIsAuthenticated(true);
            
            if (storedOrg) {
              try {
                const parsedOrg = JSON.parse(storedOrg);
                setOrganization(parsedOrg);
              } catch (err) {
                console.error('Error parsing stored organization:', err);
                sessionStorage.removeItem('organization');
              }
            }
          } catch (err) {
            console.error('Error parsing stored user:', err);
            sessionStorage.removeItem('user');
            sessionStorage.removeItem('organization');
          }
        }
      }
      setIsInitialized(true);
    };
    
    checkAuth();
  }, []);

  // Computed values must come after state variables
  const isAdmin = user?.is_admin ?? false;
  const isManager = user?.is_manager ?? false;
  const canApprove = user?.can_approve ?? false;
  const fitgapEnabled = user?.fitgap_enabled ?? true;

  const value = {
    user,
    organization,
    profilePhotoUrl,
    isAuthenticated,
    isInitialized,
    isAdmin,
    isManager,
    canApprove,
    fitgapEnabled,
    login,
    logout,
    setCurrentOrganization,
    fetchProfilePhotoUrl,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Handles use auth behavior.
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
